import { useState, useEffect, useRef } from "react";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { getGalleryItems } from "@/lib/api";
import { motion } from "framer-motion";
import { Image, X, Instagram, Facebook, Twitter, Play, Link2, Linkedin, Ghost } from "lucide-react";

const sourceIcons = {
  instagram: Instagram,
  facebook: Facebook,
  twitter: Twitter,
  tiktok: Play,
  linkedin: Linkedin,
  snapchat: Ghost,
  link: Link2,
};

const EMBED_SCRIPTS = {
  tiktok: "https://www.tiktok.com/embed.js",
  instagram: "https://www.instagram.com/embed.js",
  twitter: "https://platform.twitter.com/widgets.js",
  youtube: null, // YouTube uses iframes directly, no script needed
};

function EmbedRenderer({ html, platform, className }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!html || !containerRef.current) return;
    const node = containerRef.current;

    // Strip script tags from HTML, we'll load them manually
    const cleanHtml = html.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "");
    node.innerHTML = cleanHtml;

    // Load platform embed script
    const scriptSrc = EMBED_SCRIPTS[platform];
    if (!scriptSrc) return;

    // Remove old script if present to force re-processing
    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);
    if (existingScript) existingScript.remove();

    const script = document.createElement("script");
    script.src = scriptSrc;
    script.async = true;
    document.body.appendChild(script);

    // Watch for iframes created by the embed script and ensure audio permissions
    const observer = new MutationObserver(() => {
      const iframes = node.querySelectorAll("iframe");
      iframes.forEach((iframe) => {
        if (!iframe.getAttribute("allow")?.includes("autoplay")) {
          iframe.setAttribute("allow", "autoplay; encrypted-media; fullscreen");
        }
      });
    });
    observer.observe(node, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      node.innerHTML = "";
    };
  }, [html, platform]);

  if (!html) return null;
  return <div ref={containerRef} className={className} />;
}

export default function GalleryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const data = await getGalleryItems(filter === "all" ? null : filter);
        setItems(data);
      } catch (error) {
        console.error("Error fetching gallery:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, [filter]);

  const filters = [
    { key: "all", label: "All" },
    { key: "upload", label: "Photos" },
    { key: "instagram", label: "Instagram" },
    { key: "facebook", label: "Facebook" },
    { key: "twitter", label: "Twitter" },
    { key: "tiktok", label: "TikTok" },
    { key: "linkedin", label: "LinkedIn" },
    { key: "snapchat", label: "Snapchat" },
  ];

  // Build VideoObject JSON-LD for all video items (Google Search Console "video indexing")
  const videoSchema = (() => {
    const videoItems = items.filter((i) => i.media_type === "video" && i.media_url);
    if (videoItems.length === 0) return null;
    return {
      "@context": "https://schema.org",
      "@graph": videoItems.map((v) => ({
        "@type": "VideoObject",
        "name": v.title || "Prime Media Wireless Gallery",
        "description": v.description || v.title || "Video from Prime Media Wireless retail locations.",
        "thumbnailUrl": v.image_url || "https://primemediawireless.com/og-default.jpg",
        "contentUrl": v.media_url,
        "uploadDate": v.created_at || v.date || "2025-01-01",
        "publisher": {
          "@type": "Organization",
          "name": "Prime Media Wireless",
          "logo": { "@type": "ImageObject", "url": "https://primemediawireless.com/logo512.png" },
        },
      })),
    };
  })();

  return (
    <PublicLayout>
      {videoSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(videoSchema) }}
        />
      )}
      {/* Hero */}
      <section className="bg-navy py-20" data-testid="gallery-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="label-style text-teal mb-4">Gallery</p>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl text-white mb-6">
              Our Photo Gallery
            </h1>
            <p className="text-slate-300 text-lg font-body">
              Browse photos from our stores, events, and social media.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-slate-200 py-4" data-testid="gallery-filters">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2">
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => { setLoading(true); setFilter(f.key); }}
                className={`px-4 py-2 rounded-full text-sm font-body transition-colors ${
                  filter === f.key
                    ? "bg-teal text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid={`gallery-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 bg-slate-50" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal mx-auto"></div>
              <p className="text-slate-500 mt-4 font-body">Loading gallery...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <Image className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-body text-lg">No photos yet</p>
              <p className="text-slate-400 text-sm font-body mt-1">Check back soon!</p>
            </div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
              {items.map((item, index) => {
                const SourceIcon = sourceIcons[item.source];
                const isSocial = item.media_type === "social" || item.media_type === "embed";
                const isVideo = item.media_type === "video";
                const isPlayable = isSocial || isVideo;
                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="break-inside-avoid"
                  >
                    <div
                      className="relative group cursor-pointer rounded-xl overflow-hidden bg-slate-200"
                      onClick={() => setSelectedItem(item)}
                      data-testid={`gallery-item-${item.id}`}
                    >
                      {isVideo && !item.image_url && !item.thumbnail_url ? (
                        <video src={item.media_url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${item.media_url}` : item.media_url}
                          preload="metadata" muted playsInline className="w-full aspect-video object-cover bg-black" />
                      ) : <img
                        src={item.image_url || item.thumbnail_url}
                        alt={item.title || "Gallery photo"}
                        className="w-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />}
                      {/* Play button overlay for video/social items */}
                      {isPlayable && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="w-14 h-14 bg-black/50 rounded-full flex items-center justify-center group-hover:bg-teal/80 transition-colors duration-300">
                            <Play className="w-7 h-7 text-white ml-1" fill="white" />
                          </div>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          {item.title && (
                            <p className="text-white font-heading font-semibold text-sm mb-1">{item.title}</p>
                          )}
                          {item.description && (
                            <p className="text-white/80 text-xs font-body line-clamp-2">{item.description}</p>
                          )}
                        </div>
                        {SourceIcon && (
                          <div className="absolute top-3 right-3">
                            <SourceIcon className="w-5 h-5 text-white" />
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedItem && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedItem(null)}
          data-testid="gallery-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-slate-300 transition-colors z-10"
            onClick={() => setSelectedItem(null)}
            data-testid="gallery-lightbox-close"
          >
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-4xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            {/* Social embed */}
            {(selectedItem.media_type === "social" || selectedItem.media_type === "embed") && selectedItem.embed_html ? (
              <EmbedRenderer
                html={selectedItem.embed_html}
                platform={selectedItem.embed_platform}
                className="w-full flex items-center justify-center [&>blockquote]:max-w-[500px] [&>iframe]:max-w-full [&>iframe]:max-h-[80vh] [&>iframe]:rounded-lg"
              />
            ) : selectedItem.media_type === "video" && selectedItem.media_url ? (
              <video
                src={selectedItem.media_url}
                poster={selectedItem.image_url || "https://primemediawireless.com/og-default.jpg"}
                controls
                className="max-w-full max-h-[80vh] mx-auto rounded-lg"
              />
            ) : (selectedItem.media_type === "embed" || selectedItem.media_type === "social") && selectedItem.media_url && !selectedItem.embed_html ? (
              <div className="text-center">
                {selectedItem.image_url && (
                  <img src={selectedItem.image_url} alt={selectedItem.title || ""} className="max-w-full max-h-[60vh] object-contain rounded-lg mx-auto mb-4" />
                )}
                <a
                  href={selectedItem.media_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-teal text-white rounded-full hover:bg-teal/90 transition-colors font-body"
                  data-testid="gallery-watch-external"
                >
                  <Play className="w-5 h-5" /> Watch on {selectedItem.embed_platform || "source"}
                </a>
              </div>
            ) : (
              <img
                src={selectedItem.image_url}
                alt={selectedItem.title || "Gallery photo"}
                className="max-w-full max-h-[80vh] object-contain rounded-lg mx-auto"
              />
            )}
            {(selectedItem.title || selectedItem.description) && (
              <div className="mt-4 text-center">
                {selectedItem.title && (
                  <p className="text-white font-heading font-semibold text-lg">{selectedItem.title}</p>
                )}
                {selectedItem.description && (
                  <p className="text-white/70 font-body text-sm mt-1">{selectedItem.description}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </PublicLayout>
  );
}
